/**
 * PYP pieces shared by the teacher (B.26–B.29), coordinator (A.5a), student and family views.
 *
 * D-3: nothing here can show a mark, a band or a percentage of attainment. Evidence carries the
 * five essential elements and Learner Profile attributes; a portfolio carries narratives.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { Chip, S } from '../../design/kit';

export const ELEMENTS = ['Knowledge', 'Concepts', 'Skills', 'Attitudes', 'Action'] as const;
export type Element = (typeof ELEMENTS)[number];

export interface Evidence {
  id: string;
  kind: 'photo' | 'audio' | 'text';
  title: string;
  text: string | null;
  mediaId: string | null;
  elements: Element[];
  learnerProfile: string[];
  note: string;
  at: string;
  unitPlanId: string;
  engagementId: string;
  sample: boolean;
  unit?: string;
}

export interface Piece {
  id: string;
  title: string;
  narrative: string;
  chosenBy: 'student' | 'teacher';
  shared: boolean;
  at: string;
  theme: string;
  unit: string;
  evidence: Evidence | null;
}

export interface PortfolioView {
  student: { id: string; name: string };
  classes: { id: string; name: string; grade: string }[];
  pieces: Piece[];
  breadth: Record<Element, number>;
  thin: Element | null;
  exhibition: { done: number; total: number; next: string | null; dueAt: string | null; projectId: string | null } | null;
  available: Evidence[];
  allShared: boolean;
}

const KIND_LABEL: Record<Evidence['kind'], string> = { photo: 'Photo', audio: 'Audio', text: 'Written' };

export function PypBanner({ grade }: { grade?: string }) {
  return (
    <div role="note" style={{ ...S.panel, ...S.between, flexWrap: 'wrap', background: 'var(--c-surface-3)' }}>
      <span style={S.small}>PYP mode — set at class setup. No marks, bands or exams. Evidence is tagged to the five essential elements and the Learner Profile.</span>
      {grade ? <Chip tone="brand">PYP · {grade}</Chip> : null}
    </div>
  );
}

export function ElementChips({ elements }: { elements: string[] }) {
  if (!elements.length) return <span style={S.mutedXs}>—</span>;
  return <span className="chip-row" style={{ display: 'inline-flex', flexWrap: 'wrap', gap: '4px' }}>{elements.map((e) => <Chip key={e} tone="brand">{e}</Chip>)}</span>;
}

export function LpChips({ attributes }: { attributes: string[] }) {
  if (!attributes.length) return <span style={S.mutedXs}>—</span>;
  return <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: '4px' }}>{attributes.map((a) => <Chip key={a}>{a}</Chip>)}</span>;
}

/** Photos and recordings come through the API with the bearer token, so fetch to a blob. */
const TILE = { width: '112px', height: '84px', borderRadius: '8px', flex: 'none', objectFit: 'cover', display: 'grid', placeItems: 'center', textAlign: 'center', background: 'var(--c-surface-3)', padding: 0 } as const;

/** `thumb`: the B.28 piece card's thumbnail tile — a fixed tile even when there is no file to show. */
export function MediaPreview({ evidence, thumb }: { evidence: Pick<Evidence, 'kind' | 'mediaId' | 'text' | 'sample' | 'title'>; thumb?: boolean }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!evidence.mediaId || evidence.kind === 'text') return;
    let live = true;
    let made: string | null = null;
    api.blob(`/media/${evidence.mediaId}`).then((u) => {
      made = u;
      if (live) setUrl(u);
    }).catch(() => live && setFailed(true));
    return () => {
      live = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [evidence.mediaId, evidence.kind]);

  if (evidence.kind === 'text') return evidence.text ? <p style={{ ...S.small, margin: 0, whiteSpace: 'pre-wrap' }}>{evidence.text}</p> : null;
  const note = (t: string) => (thumb ? <span style={{ ...TILE, ...S.mutedXs }}>{t}</span> : <span style={S.mutedXs}>{t}</span>);
  if (!evidence.mediaId) return note(evidence.sample ? (thumb ? 'Sample photo' : 'Sample entry · no file attached') : 'No file attached');
  if (failed) return note('The file could not be opened.');
  if (!url) return note('Loading…');
  return evidence.kind === 'photo'
    ? <img src={url} alt={evidence.title} style={thumb ? TILE : { maxWidth: '100%', maxHeight: '240px', borderRadius: '8px', display: 'block' }} />
    : <audio controls src={url} style={{ width: '100%' }} aria-label={evidence.title} />;
}

export function kindLabel(e: Pick<Evidence, 'kind'> | null | undefined): string {
  return e ? KIND_LABEL[e.kind] : '';
}

/** B.28 right column: curated pieces per element this term — counts, never a score. */
export function BreadthCounts({ counts, thin, name }: { counts: Record<Element, number>; thin: Element | null; name: string }) {
  const max = Math.max(1, ...Object.values(counts));
  return (
    <section aria-label="Evidence breadth" style={{ ...S.card, display: 'grid', gap: '10px' }}>
      <div>
        <h2 style={{ ...S.h3, margin: 0 }}>Evidence breadth</h2>
        <div style={S.sub}>Pieces tagged to each element this term</div>
      </div>
      {ELEMENTS.map((e) => (
        <div key={e} style={{ display: 'grid', gridTemplateColumns: '90px minmax(0, 1fr) 24px', alignItems: 'center', gap: '8px' }}>
          <span style={S.small}>{e}</span>
          <span aria-hidden="true" style={{ height: '6px', borderRadius: '3px', background: 'var(--c-surface-3)', overflow: 'hidden' }}>
            <span style={{ display: 'block', height: '100%', width: `${(counts[e] / max) * 100}%`, background: 'var(--c-brand)' }} />
          </span>
          <span style={{ ...S.small, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{counts[e]}</span>
        </div>
      ))}
      {thin ? <p style={{ ...S.mutedXs, margin: 0 }}>{thin} is thin. Look for a chance for {name.split(' ')[0]} to add some in the next engagement.</p> : null}
    </section>
  );
}

export function ExhibitionCard({ exhibition, to }: { exhibition: NonNullable<PortfolioView['exhibition']>; to?: string }) {
  return (
    <section aria-label="PYP Exhibition" style={{ ...S.card, display: 'grid', gap: '6px' }}>
      <span style={S.sectionLabel}>Projects · PYP Exhibition</span>
      <h2 style={{ ...S.h3, margin: 0 }}>Grade 5 Exhibition</h2>
      <p style={{ ...S.mutedXs, margin: 0 }}>Lives under Projects. Only shown to Grade 5 PYP students.</p>
      <div style={S.between}>
        <span style={S.small}>Milestones</span>
        <span style={{ ...S.small, fontWeight: 600 }}>{exhibition.done} of {exhibition.total}</span>
      </div>
      {exhibition.next ? <span style={S.mutedXs}>Next: {exhibition.next}</span> : null}
      {to ? <Link to={to} style={S.link}>Open the Exhibition</Link> : null}
    </section>
  );
}

/** One curated piece, as the designs draw it: what it is, the narrative, its tags. */
export function PieceCard({ piece, onRemove }: { piece: Piece; onRemove?: () => void }) {
  const e = piece.evidence;
  const photo = e?.kind === 'photo';
  return (
    <article style={{ ...S.card, display: 'grid', gap: '8px', padding: '16px 18px' }}>
      <div style={{ ...S.between, alignItems: 'flex-start', gap: '12px' }}>
        {photo ? <MediaPreview evidence={e} thumb /> : null}
        <div style={{ minWidth: 0, flex: 1 }}>
          <h3 style={{ ...S.h3, margin: 0 }}>{piece.title}</h3>
          <div style={S.sub}>{[kindLabel(e), piece.unit].filter(Boolean).join(' · ')}</div>
        </div>
        <span style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {piece.theme ? <Chip>{piece.theme}</Chip> : null}
          {piece.shared ? <Chip tone="good">Shared with family</Chip> : null}
        </span>
      </div>
      <p style={{ ...S.small, margin: 0 }}>{piece.narrative}</p>
      {e && !photo ? <MediaPreview evidence={e} /> : null}
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
        {e ? <ElementChips elements={e.elements} /> : null}
        {e ? <LpChips attributes={e.learnerProfile} /> : null}
        {onRemove ? <button type="button" className="kit-btn kit-btn--ghost" style={{ marginLeft: 'auto', fontSize: '0.75rem', padding: '0 8px', minHeight: '28px' }} onClick={onRemove}>Remove</button> : null}
      </div>
    </article>
  );
}

/** Toggle chips for a closed list (elements, attributes). Buttons with aria-pressed. */
export function ToggleChips({ label, options, value, onChange }: { label: string; options: readonly string[]; value: string[]; onChange: (v: string[]) => void }) {
  return (
    <fieldset style={{ border: 'none', padding: 0, margin: 0, display: 'grid', gap: '6px' }}>
      <legend style={{ ...S.sectionLabel, marginBottom: '6px' }}>{label}</legend>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
        {options.map((o) => {
          const on = value.includes(o);
          return (
            <button
              key={o}
              type="button"
              aria-pressed={on}
              onClick={() => onChange(on ? value.filter((x) => x !== o) : [...value, o])}
              style={{
                fontSize: '0.75rem', fontWeight: 600, padding: '6px 10px', borderRadius: '6px', cursor: 'pointer', minHeight: '32px',
                border: `1px solid ${on ? 'var(--c-brand)' : 'var(--c-line)'}`, background: on ? 'var(--c-brand-wash)' : '#fff', color: on ? 'var(--c-brand)' : 'var(--c-ink-3)',
              }}
            >
              {o}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}
